export const saveFile = (filename: string, fileContent: string) => {
    const bb = new Blob([fileContent], { type: 'text/plain' });
    const a = document.createElement('a');
    a.download = filename + '.mcr';
    a.href = window.URL.createObjectURL(bb);
    a.click();
};

export const removeItem = <T>(arr: Array<T>, value: T): Array<T> => {
    const index = arr.indexOf(value);
    if (index > -1) {
        arr.splice(index, 1);
    }
    return arr;
};

export const isLocalEnv = () => (process.env.REACT_APP_ENVIRONMENT as string) === 'local';

export const isDevelopmentEnv = () => (process.env.REACT_APP_ENVIRONMENT as string) === 'dev';

export const isProductionEnv = () => (process.env.REACT_APP_ENVIRONMENT as string) === 'prod';

export const roundToFour = (num: number): number => {
    return parseFloat(num.toFixed(4));
};

export const addIdToFile = (filename: string) => {
    const id = Math.random().toString(36).substring(2, 10);

    const filenameParts = filename.split('.');
    const extension = filenameParts.length > 1 ? filenameParts.pop() : '';

    const newFilename = `${filenameParts.join('.')}_${id}.${extension}`;

    return newFilename;
};

export const roundToTwo = (num: number | undefined): number => {
    if (!num) return 0;
    return parseFloat(num.toFixed(2));
};

export const roundNumbers = <T extends object | null | undefined>(obj: T): T | undefined => {
    const roundNumber = (value: unknown) => {
        if (typeof value === 'number') {
            return Math.round(value * 100) / 100;
        } else {
            return value;
        }
    };
    const roundObject = (obj: Record<string, unknown>): Record<string, unknown> => {
        const _obj = JSON.parse(JSON.stringify(obj));
        for (const key in _obj) {
            if (_obj.hasOwnProperty(key)) {
                if (typeof _obj[key] === 'object' && _obj[key] !== null) {
                    _obj[key] = roundObject(_obj[key] as Record<string, unknown>);
                } else {
                    // ⚠️ CRITICAL: DO NOT round layer coefficients (a1, a2, a3) - Excel requires 3 decimal precision
                    const excludeKeys = ['id', 'a1', 'a2', 'a3'];
                    _obj[key] = excludeKeys.includes(key.toLowerCase()) ? _obj[key] : roundNumber(_obj[key]);
                }
            }
        }
        return _obj;
    };
    return obj ? (roundObject(obj as Record<string, unknown>) as T) : undefined;
};

/** A finite number is a real input, zero included (Excel uses it); absent, empty or non-numeric values take the fallback. */
export const finiteOr = (value: unknown, fallback: number): number => (typeof value === 'number' && Number.isFinite(value) ? value : fallback);

// Import logging configuration types (DEPRECATED - kept for backward compatibility)
import { LogTag } from '../config/loggingConfig';

// ============================================================================
// SIMPLIFIED LOGGING SYSTEM WITH COMPONENT FILTER
// ============================================================================

// DYNAMIC LOG_FILTER - Gets value from URL params or fallback to static config
const getLogFilter = (): string => {
    // Simple hardcoded filter - change this string to control what gets logged
    return 'DEBUG:'; // Change this to enable specific debug logs (e.g., 'PDF', 'unit', 'EsalCalculationModal')
};

// Export for backward compatibility
export const LOG_FILTER: string = getLogFilter();

// Examples:
// const LOG_FILTER = "PDF";                        // Only PDF components
// const LOG_FILTER = "Form|Input";                 // Form and Input components
// const LOG_FILTER = "^Geogrid";                   // Components starting with Geogrid
// const LOG_FILTER = "validation|error";           // Validation and error components

/**
 * Checks if logging should be enabled based on component name filter
 * @param component - Component name for filtering
 * @param _tag - DEPRECATED: No longer used, kept for backward compatibility
 * @returns true if logging should be enabled, false otherwise
 */
const shouldLog = (component: string, _tag?: LogTag): boolean => {
    // Disable logs in production environment
    if (process.env.NODE_ENV === 'production') {
        return false;
    }

    // Disable logs if running on production URL
    if (typeof window !== 'undefined' && window.location) {
        const hostname = window.location.hostname;
        if (hostname === 'mactest.netkom.it') {
            return false;
        }
    }

    // Get current filter (hardcoded in getLogFilter)
    const currentFilter = getLogFilter();

    // No filter = no logs (silent by default)
    if (!currentFilter || currentFilter.length === 0) {
        return false;
    }

    // Special cases: ALL or TUTTI = show everything
    if (currentFilter.toLowerCase() === 'all' || currentFilter.toLowerCase() === 'tutti') {
        return true;
    }

    const regex = new RegExp(currentFilter, 'i');

    // Match against component name only
    return regex.test(component);
};

/**
 * Conditional logging helper
 * @param component - Component or module name for context
 * @param message - Log message
 * @param data - Optional data to log
 * @param tag - DEPRECATED: No longer used, kept for backward compatibility
 */
export const logDebug = (component: string, message: string, data?: unknown, tag?: LogTag): void => {
    if (shouldLog(component, tag)) {
        const formattedMessage = `[${component}] ${message}`;
        if (data !== undefined) {
            console.log(formattedMessage, data);
        } else {
            console.log(formattedMessage);
        }
    }
};

/**
 * Conditional error logging helper
 * @param component - Component or module name for context
 * @param message - Error message
 * @param error - Optional error object to log
 * @param tag - DEPRECATED: No longer used, kept for backward compatibility
 */
export const logError = (component: string, message: string, error?: unknown, tag?: LogTag): void => {
    if (shouldLog(component, tag)) {
        const formattedMessage = `[${component}] ${message}`;
        if (error !== undefined) {
            console.error(formattedMessage, error);
        } else {
            console.error(formattedMessage);
        }
    }
};

/**
 * Conditional warning logging helper
 * @param component - Component or module name for context
 * @param message - Warning message
 * @param data - Optional data to log
 * @param tag - DEPRECATED: No longer used, kept for backward compatibility
 */
export const logWarn = (component: string, message: string, data?: unknown, tag?: LogTag): void => {
    if (shouldLog(component, tag)) {
        const formattedMessage = `[${component}] ${message}`;
        if (data !== undefined) {
            console.warn(formattedMessage, data);
        } else {
            console.warn(formattedMessage);
        }
    }
};

/**
 * Info level logging helper
 * @param component - Component or module name for context
 * @param message - Info message
 * @param data - Optional data to log
 * @param tag - DEPRECATED: No longer used, kept for backward compatibility
 */
export const logInfo = (component: string, message: string, data?: unknown, tag?: LogTag): void => {
    if (shouldLog(component, tag)) {
        const formattedMessage = `[${component}] ${message}`;
        if (data !== undefined) {
            console.info(formattedMessage, data);
        } else {
            console.info(formattedMessage);
        }
    }
};

// Export the LogTag enum for backward compatibility (DEPRECATED)
export { LogTag };
